import { useEffectWithController } from '@carvana/merch-react-hooks';
import { isAxiosError } from 'axios';
import { useContext, useRef } from 'react';
import { httpWithAuth } from '~/clients';
import { VEHICLE_PRICING } from '~/constants/endpoints';
import { useResolvedOnServer } from '~/context/common/DeferredResolutionContext';
import CommonStaticContext from '~/context/common/StaticContext';
import TradeContext, {
  useGetAcquisitionNameForApiRequest,
  useIsTradeInApplied
} from '~/context/common/TradeContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useSoonestRoute } from '~/context/vdp/DeliveryPickupContext';
import VdpStaticContext from '~/context/vdp/StaticContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import {
  useGetVehicleDetails,
  useGetVehicleId
} from '~/context/vdp/VehicleContext';
import useSkipFirstFetchIfResolvedOnServer from '~/hooks/common/useSkipFirstFetchIfResolvedOnServer';
import useDeliveryPickupLoadedOrWillNotLoad from '~/hooks/vdp/selectors/useDeliveryPickupLoadedOrWillNotLoad';
import useIsHighNetTrade from '~/hooks/vdp/useIsHighNetTrade';
import {
  nonSearchableError,
  PricingError,
  type VehiclePricingResponse
} from '~/services/common/contracts/PricingResponse';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetVehiclePricing() {
  const { clientId, eventCorrelationId } = useContext(CommonStaticContext);
  const {
    trades: { isLoaded: isTradesLoaded }
  } = useContext(TradeContext);
  const { pricing: pricingResolvedOnServer } = useResolvedOnServer();
  const {
    fetchVehiclePricingFlag,
    setPricingData,
    setPricingError,
    vehiclePricingInfo: { setError, setLoaded, setLoading }
  } = useContext(UserVehicleContext);
  const shouldSkipFetch = useSkipFirstFetchIfResolvedOnServer(
    pricingResolvedOnServer
  );
  const lastFetchFlag = useRef(fetchVehiclePricingFlag);
  const { utmParams } = useContext(VdpStaticContext);

  const vehicleId = useGetVehicleId();
  const { saleStatus = '' } = useGetVehicleDetails();
  const location = useUserLocation();
  const applyTradeIn = useIsTradeInApplied();
  const preferredAcquisitionName = useGetAcquisitionNameForApiRequest();
  const isHighNetTrade = useIsHighNetTrade();
  const isDeliveryPickupLoadedOrWillNotLoad =
    useDeliveryPickupLoadedOrWillNotLoad();
  const routeId = useSoonestRoute()?.routeId;
  const shouldApplyTradeIn = isHighNetTrade ? false : applyTradeIn;

  useEffectWithController(
    controller => {
      if (saleStatus === 'Sold') {
        setLoaded();

        return;
      }

      if (!isDeliveryPickupLoadedOrWillNotLoad || !isTradesLoaded) {
        return;
      }

      const wasTriggered = fetchVehiclePricingFlag !== lastFetchFlag.current;
      lastFetchFlag.current = fetchVehiclePricingFlag;

      // A flag bump (e.g. a manual retry from the soft-pull modal or co-applicant toggle) always
      // fetches, even during the skip window for a server-resolved deferred value.
      if (shouldSkipFetch() && !wasTriggered) {
        return;
      }

      setLoading();
      setPricingError(PricingError.None);

      httpWithAuth
        .post<VehiclePricingResponse>(
          VEHICLE_PRICING,
          {
            applyVerboseLogging: false,
            applyTradeIn: shouldApplyTradeIn,
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            routeId,
            vehicleId: vehicleId,
            zip5: location.zip,
            preferredAcquisitionName: isHighNetTrade
              ? ''
              : preferredAcquisitionName,
            analyticsData: {
              //this is a required field for the endpoint we can send the rest of the data as empty. Ask the BE team if they need more data in this object.
              clientId,
              correlationId: eventCorrelationId,
              utmParams
            }
          },
          {
            signal: controller.signal
          }
        )
        .then(({ data }) => {
          setPricingData(data);
        })
        .catch(
          generateErrorLogger(
            'Get VDP Pricing',
            {},
            {
              callback: error => {
                setError(error);
                // if the vehicle is not in elastic search the endpoint will 404 and return an errorMessage in response
                const errorMessage = isAxiosError(error)
                  ? error.response?.data ?? ''
                  : '';
                setPricingError(
                  errorMessage === nonSearchableError
                    ? PricingError.NonSearchable
                    : PricingError.ApiError
                );
              },
              controller
            }
          )
        );
    },
    [
      applyTradeIn,
      clientId,
      eventCorrelationId,
      fetchVehiclePricingFlag,
      isDeliveryPickupLoadedOrWillNotLoad,
      isHighNetTrade,
      isTradesLoaded,
      location.zip,
      preferredAcquisitionName,
      routeId,
      saleStatus,
      setError,
      setLoaded,
      setLoading,
      setPricingData,
      setPricingError,
      shouldApplyTradeIn,
      shouldSkipFetch,
      utmParams,
      vehicleId
    ]
  );
}
